"use client";

import React, { useEffect, useRef, useState } from "react";
import { useInteractionContext } from "@/context/InteractionContext";

const isTouchDevice = () =>
  typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const LERP_FACTOR = 0.15;
const SIZE_DEFAULT = 8;
const SIZE_HOVER = 16;

const CursorFX: React.FC = () => {
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [hidden, setHidden] = useState<boolean>(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const { manager } = useInteractionContext();

  // lerp 用の現在位置を保持
  const posRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const hoveringRef = useRef(false);

  // prefers-reduced-motion チェック
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // リンクホバー検出
  useEffect(() => {
    if (isTouchDevice() || reducedMotion) return;

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive = target.closest("a, button, [role='button'], input, textarea, select, [data-cursor-hover]");
      hoveringRef.current = !!isInteractive;
      if (innerRef.current) {
        const size = isInteractive ? SIZE_HOVER : SIZE_DEFAULT;
        innerRef.current.style.width = `${size}px`;
        innerRef.current.style.height = `${size}px`;
        innerRef.current.style.borderColor = isInteractive
          ? "rgba(255,255,255,0.6)"
          : "rgba(255,255,255,0.4)";
      }
    };

    document.addEventListener("mouseover", handleMouseOver);
    return () => document.removeEventListener("mouseover", handleMouseOver);
  }, [reducedMotion]);

  useEffect(() => {
    if (isTouchDevice() || reducedMotion) {
      setHidden(true);
      return;
    }

    let raf = 0;
    let initialized = false;

    const loop = () => {
      if (!cursorRef.current || !manager) {
        raf = requestAnimationFrame(loop);
        return;
      }

      const state = manager.getMouseState();
      const doc = document.documentElement;
      const width = doc.clientWidth || window.innerWidth;
      const height = doc.clientHeight || window.innerHeight;
      const tx = ((state.normalizedX + 1) / 2) * width;
      const ty = ((-state.normalizedY + 1) / 2) * height;

      targetRef.current.x = tx;
      targetRef.current.y = ty;

      if (!initialized) {
        posRef.current.x = tx;
        posRef.current.y = ty;
        initialized = true;
      }

      // lerp でスムーズ追従
      posRef.current.x += (targetRef.current.x - posRef.current.x) * LERP_FACTOR;
      posRef.current.y += (targetRef.current.y - posRef.current.y) * LERP_FACTOR;

      cursorRef.current.style.transform = `translate3d(${posRef.current.x}px, ${posRef.current.y}px, 0)`;
      setHidden(false);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const handleLeave = () => setHidden(true);
    const handleEnter = () => setHidden(false);
    window.addEventListener("mouseleave", handleLeave);
    window.addEventListener("mouseenter", handleEnter);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mouseleave", handleLeave);
      window.removeEventListener("mouseenter", handleEnter);
    };
  }, [reducedMotion, manager]);

  return (
    <div
      ref={cursorRef}
      aria-hidden
      className={`fixed pointer-events-none z-[100] ${
        hidden ? "opacity-0" : "opacity-100"
      }`}
      style={{ left: 0, top: 0, transition: "opacity 0.2s ease" }}
    >
      <div
        ref={innerRef}
        className="-translate-x-1/2 -translate-y-1/2 rounded-full border mix-blend-difference"
        style={{
          width: `${SIZE_DEFAULT}px`,
          height: `${SIZE_DEFAULT}px`,
          borderColor: "rgba(255,255,255,0.4)",
          backgroundColor: "transparent",
          transition: "width 0.2s ease, height 0.2s ease, border-color 0.2s ease",
        }}
      />
    </div>
  );
};

export default CursorFX;
